import {computed, inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {ServerAddress} from '../../models/server-address.model';
import {firstValueFrom, throwError, timeout} from 'rxjs';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {unsecureProtocol} from '../../constants/server/unsecure-protocol.constant';
import {secureProtocol} from '../../constants/server/secure-protocol.constant';
import {extractHostname} from '../../util/extract-hostname.util';
import {ServerInformation} from '../../bfa-api/models/server-information';
import {TimeoutError} from '../../errors/timeout-error';
import {HttpError} from '../../errors/http-error';
import {LanguageService} from '../language/language.service';
import {API_CONFIGS} from '../../../configs/api.config';
import {ServerTypes} from '../../types/server/server-types.type';
import {ServerConfig} from '../../models/configs/server.config';
import {STATIC_CONFIGS} from '../../../configs/static.config';
import {UrlParts} from '../../models/url/url-parts.model';
import {AppStorageIdEnum} from '../../enums/app-storage-id.enum';
import {AppStorageService} from '../app-storage/app-storage.service';
import {AppStorage} from 'src/app/models/app-storage.model';
import {ServerSelectionNoApiError, ServerSelectionNotMatchingVersionError} from '../../errors/server-selection-errors';
import {SslCertificateError} from '../../errors/ssl-certificate-error';
import {SslCertificateService} from '../ssl-certificate/ssl-certificate.service';
import {Capacitor} from '@capacitor/core';
import {AmagnoStorageKeyEnum} from '../../enums/amagno-storage-key.enum';
import {AdvancedServerInformation} from '../../models/server/advanced-server-information.model';
import {ServerTypeEnum} from '../../enums/server-type.enum';

@Injectable({
    providedIn: 'root'
})
export class ServerSelectionService {
    private readonly currentServerHostname: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    private readonly serverAddresses: WritableSignal<Array<ServerAddress>>;
    private readonly isCheckingForVersion: WritableSignal<boolean>;
    private readonly selectedServer: Signal<ServerAddress | undefined>;
    private readonly httpClient = inject(HttpClient);
    private readonly languageService = inject(LanguageService);
    private readonly appStorageService = inject(AppStorageService);
    private readonly sslCertificateService = inject(SslCertificateService);
    private readonly storage: AppStorage;

    public constructor() {
        this.isCheckingForVersion = signal(true);
        this.storage = this.appStorageService.getStorage(AppStorageIdEnum.AMAGNO);
        this.serverAddresses = signal(this.storage.get(AmagnoStorageKeyEnum.SERVERS) || []);
        this.selectedServer = computed(() => this.serverAddresses()
            .find(server => server.selected));
    }

    public static createEmptyServerAddress(): ServerAddress {
        return {
            loginCount: 0,
            url: '',
            displayUrl: '',
            selected: false,
            name: '',
            successfulLoggedIn: false,
            isOffline: false,
            isIncompatible: false,
            serverInformation: {
                serverVersion: '',
                maintenanceMessage: '',
                notification: undefined,
                registrationLinkUrl: '',
                systemMessage: '',
                apiVersion: ''
            },
            currentNotificationId: undefined,
            notificationReadByUserIds: []
        };
    }

    public toggleVersionCheck(): void {
        this.isCheckingForVersion.update(isCheckingForVersion => !isCheckingForVersion);
    }

    public loadServers(): void {
        const serverAddresses: Array<ServerAddress> = this.getServerAddresses();
        const needToSave = this.fixInsecureServerProtocols(serverAddresses);
        if (needToSave) {
            this.serverAddresses.set(serverAddresses);
            this.saveServers();
        }
    }

    public async addServer(serverAddress: ServerAddress): Promise<ServerAddress> {
        const response = await this.getServerInformationAndWorkingUrlForServer(serverAddress.url);
        if (!this.checkApiVersion(response.serverInformation.apiVersion)) {
            throw (new ServerSelectionNotMatchingVersionError());
        }
        serverAddress.serverInformation = response.serverInformation;
        serverAddress.url = response.workingServerUrl;
        this.addServerAddressToList(serverAddress);
        return serverAddress;
    }

    public async selectServer(displayUrl: string): Promise<void> {
        const currentServer = this.getSelectedServer();
        if (currentServer?.displayUrl !== displayUrl) {
            this.currentServerHostname.set(extractHostname(currentServer?.displayUrl));
        }
        let selectedServerAddress: ServerAddress | undefined;
        this.serverAddresses.update(serverAddresses => serverAddresses.map(server => {
            const isSelectedServer = server.displayUrl === displayUrl;
            server.selected = isSelectedServer;
            selectedServerAddress = isSelectedServer ? server : selectedServerAddress;
            return server;
        }));

        if (selectedServerAddress) {
            await this.fetchServerInformationAndGetUpdatedServerAddress(selectedServerAddress);
        }
    }

    public hasServerHost(url: string): boolean {
        return this.getServerAddresses()
            .some(serverAddress => this.cleanUrl(serverAddress.displayUrl) === this.cleanUrl(url));
    }

    public removeServer(displayUrl: string): void {
        const filteredServerAddresses = this.getServerAddresses()
            .filter(server => server.displayUrl !== displayUrl);
        this.updateServerAddresses(filteredServerAddresses);
    }

    public cleanUrl(url: string, removeProtocol: boolean = true): string {
        const urlParts = this.getUrlPartsByUrl(url);
        let cleanUrl;

        if (urlParts.host) {
            cleanUrl = urlParts.host;

            if (urlParts.port) {
                cleanUrl += ':' + urlParts.port;
            }

            cleanUrl += urlParts.path;
        } else {
            cleanUrl = url;
        }

        cleanUrl = cleanUrl
            .replace(/\/+/g, '/')
            .replace(/(\/$)|(^\/)/g, '')
            // Remove internal-API path segment from server URLs, due to downward compatibility
            // @see https://amagno.atlassian.net/browse/APP-1024
            .replace(new RegExp('/' + API_CONFIGS.apis.internal.path + '$'), '');

        if (!removeProtocol && urlParts.protocol) {
            cleanUrl = urlParts.protocol + '://' + cleanUrl;
        }

        return cleanUrl;
    }

    public addDefaultServerAddresses(): void {
        const defaultServers = this.getDefaultServers();
        this.updateServerAddresses(defaultServers);
    }

    /**
     * @throws TimeoutError on API connection timeout of last tried connection endpoint
     * @throws HttpError when last tried connection endpoint returns an error
     * @throws Error on errors that occurred while computing the API response of last tried connection endpoint
     */
    public async getServerInformation(url: string, timeoutInMs?: number): Promise<ServerInformation> {
        const serverInformationUrl = url + '/' + API_CONFIGS.apis.bfa.path + '/' + API_CONFIGS.paths.serverInformation;
        return this.fetchServerInformation(serverInformationUrl, timeoutInMs);
    }

    public increaseLoginCount(displayUrl: string): void {
        const servers = this.serverAddresses();
        const selectedServer = servers.find(server => server.displayUrl === displayUrl);
        if (selectedServer) {
            selectedServer.loginCount += 1;
            this.updateServerAddresses(servers);
        }
    }

    public extractProtocolFromUrl(url: string): string | undefined {
        const protocolRegex = /https?:\/\//gm;
        const regexMatches = protocolRegex.exec(url);
        if (regexMatches) {
            return regexMatches[0];
        }
        return undefined;
    }

    public async fetchServerInformationAndGetUpdatedServerAddress(serverAddress: ServerAddress): Promise<ServerAddress | undefined> {
        try {
            const serverInformation = await this.getServerInformation(serverAddress.url);
            this.updateServerAddressByServerInformation(serverAddress, serverInformation);
            return this.getServerAddressByDisplayUrl(serverAddress.displayUrl);
        } catch {
            this.updateServerAddressByServerInformation(serverAddress);
            return this.getServerAddressByDisplayUrl(serverAddress.displayUrl);
        }
    }

    public getServerAddressByDisplayUrl(displayUrl: string): ServerAddress | undefined {
        return this.serverAddresses()
            .find(server => server.displayUrl === displayUrl);
    }

    public getSelectedServerSignal(): Signal<ServerAddress | undefined> {
        return this.selectedServer;
    }

    public getSelectedServer(): ServerAddress | undefined {
        return structuredClone(this.selectedServer());
    }

    public getSelectedServerUrl(): string {
        return this.getSelectedServer()?.url as string;
    }

    public getIsMockServerSelected(): boolean {
        const selectedServerUrl = this.getSelectedServer()?.url;
        const mockServers = environment.mockServers as Array<ServerConfig>;
        return mockServers.some(mockServer => mockServer.url === selectedServerUrl);
    }

    public getInternalApiUrl(): string {
        return this.getSelectedServerUrl() + '/' + API_CONFIGS.apis.internal.path;
    }

    public getIsCheckingForVersionSignal(): Signal<boolean> {
        return this.isCheckingForVersion.asReadonly();
    }

    public getServerAddresses(): Array<ServerAddress> {
        return structuredClone(this.serverAddresses());
    }

    public getHasServerAddresses(): boolean {
        return this.serverAddresses().length > 0;
    }

    public updateServerAddresses(serverAddresses: Array<ServerAddress>): void {
        this.serverAddresses.set(serverAddresses);
        this.saveServers();
    }

    public updateServerAddressByServerInformation(serverAddress: ServerAddress, serverInformation?: ServerInformation): void {
        const serverAddresses = this.getServerAddresses();
        this.updateServerAddresses(serverAddresses.map(updatedServerAddress => {
            const isSelectedServer = updatedServerAddress.url === serverAddress.url;
            if (!isSelectedServer) {
                return updatedServerAddress;
            }

            if (serverInformation) {
                updatedServerAddress.serverInformation = serverInformation;
                updatedServerAddress.isOffline = false;
                updatedServerAddress.isIncompatible = !this.checkApiVersion(serverInformation.apiVersion);
                if (updatedServerAddress.currentNotificationId !== serverInformation.notification?.notificationId) {
                    updatedServerAddress.currentNotificationId = serverInformation.notification?.notificationId;
                    updatedServerAddress.notificationReadByUserIds = [];
                }
                return updatedServerAddress;
            }

            updatedServerAddress.isOffline = true;
            updatedServerAddress.isIncompatible = false;
            updatedServerAddress.serverInformation = {
                serverVersion: '',
                maintenanceMessage: undefined,
                notification: undefined,
                systemMessage: undefined,
                registrationLinkUrl: undefined,
                apiVersion: '',
            };
            return updatedServerAddress;
        }));
    }

    public sortServerAddresses(): void {
        this.serverAddresses.update(serverAddresses => {
            serverAddresses.sort((firstServerAddress: ServerAddress, secondServerAddress: ServerAddress) => (firstServerAddress.name || secureProtocol + firstServerAddress.url)
                .localeCompare((secondServerAddress.name || secureProtocol + secondServerAddress.url)));
            return serverAddresses;
        });
        this.saveServers();
    }

    public getCurrentServerHostnameSignal(): Signal<string | undefined> {
        return this.currentServerHostname.asReadonly();
    }

    private fixInsecureServerProtocols(serverAddresses: Array<ServerAddress>): boolean {
        let hasUpdatedAnyProtocol = false;
        // fix for already added connection without protocol
        for (const server of serverAddresses) {
            if (!this.urlHasValidProtocol(server.url)) {
                if (!server.displayUrl) {
                    server.displayUrl = server.url;
                }

                server.url = this.cleanUrl(secureProtocol + server.url + '/' + environment.apiUrlParts.join('/'), false);
                hasUpdatedAnyProtocol = true;
            }
        }
        return hasUpdatedAnyProtocol;
    }

    private saveServers(): void {
        this.storage.set(AmagnoStorageKeyEnum.SERVERS, this.serverAddresses());
    }

    private checkApiVersion(apiVersion: string): boolean {
        if (!this.isCheckingForVersion()) {
            return true;
        }

        const [apiVersionMajor, apiVersionMinor] = apiVersion.split('.')
            .map(Number);

        return apiVersionMajor === API_CONFIGS.requiredApiVersion.major && apiVersionMinor >= API_CONFIGS.requiredApiVersion.minor;
    }

    private urlHasValidProtocol(url: string): boolean {
        return (url.includes(secureProtocol) || url.includes(unsecureProtocol));
    }

    private getUrlObjectByCleanUrl(cleanUrl: string): URL | undefined {
        if (URL.canParse(cleanUrl)) {
            return new URL(cleanUrl);
        }

        if (URL.canParse(secureProtocol + cleanUrl)) {
            return new URL(secureProtocol + cleanUrl);
        }

        return undefined;
    }

    private createDefaultServerAddress(serverName: ServerTypes): ServerAddress | undefined {
        const server: ServerConfig = STATIC_CONFIGS.servers[serverName];
        if (server) {
            return this.createServerAddressByServerConfig(server);
        }
        return undefined;
    }

    private createServerAddressByServerConfig(serverConfig: ServerConfig): ServerAddress {
        return {
            ...ServerSelectionService.createEmptyServerAddress(),
            url: serverConfig.url,
            displayUrl: serverConfig.displayUrl,
            name: serverConfig.name
        };
    }

    private addServerAddressToList(serverAddress: ServerAddress): void {
        const serverAddresses = this.serverAddresses();
        serverAddresses.push(serverAddress);
        this.sortServerAddresses();
    }

    /**
     * @throws TimeoutError on API connection timeout
     * @throws HttpError on error response from API
     * @throws Error on errors that occurred while computing the API response
     */
    private async fetchServerInformation(url: string, timeoutInMs?: number): Promise<ServerInformation> {
        timeoutInMs = timeoutInMs || API_CONFIGS.apiTimeoutsInMs.serverInformation;

        const urlHasValidProtocol = this.urlHasValidProtocol(url);
        const currentIntlLocale = this.languageService.getCurrentIntlLocale() ?? this.languageService.getDefaultLocale();
        const serverInformationUrl = (urlHasValidProtocol ? url : secureProtocol + url) + '?locale=' + currentIntlLocale.language;

        try {
            return await firstValueFrom(this.httpClient.get<ServerInformation>(serverInformationUrl)
                .pipe(timeout({
                    each: timeoutInMs,
                    with: () => throwError(() => new TimeoutError(timeoutInMs, `Timeout error in URL: "${serverInformationUrl}" after ${timeoutInMs}ms`)),
                })));
        } catch (error) {
            if (error instanceof HttpErrorResponse) {
                if (error.status === 0 && await this.sslCertificateService.hasSslCertificateError(serverInformationUrl)) {
                    throw new SslCertificateError();
                } else {
                    throw new HttpError(error.status, error.message);
                }
            }

            throw error;
        }
    }

    private getDefaultServers(): ServerAddress[] {
        const environmentServers: Array<ServerTypes> = environment.servers as Array<ServerTypes>;
        const servers: Array<ServerAddress> = [];
        const isNativeTestApp = Capacitor.isNativePlatform() && (environment.stage === ServerTypeEnum.Dev || environment.stage === ServerTypeEnum.Test);
        for (const [index, serverName] of environmentServers.entries()) {
            const server = this.createDefaultServerAddress(serverName);
            if (server) {
                if ((isNativeTestApp && serverName === ServerTypeEnum.Test) || (!isNativeTestApp && index === 0)) {
                    server.selected = true;
                }
                servers.push(server);
            }
        }
        for (const mockServer of (environment.mockServers as Array<ServerConfig>)) {
            servers.push(this.createServerAddressByServerConfig(mockServer));
        }
        return servers;
    }

    private getUrlPartsByUrl(url: string): UrlParts {
        /**
         * What happens here? The RegExp explained...
         *
         * - protocol: Matches the URL scheme (e.g., http, https, ftp, http-alt).
         *   - [a-zA-Z][a-zA-Z0-9+\-.]+: Starts with a letter, followed by letters, digits, +, ., or -.
         *   - (?::\/\/)?: Optionally followed by ://.
         *   - The entire group is optional due to (?: ... )?.
         *
         * - host: Matches the hostname or IP address.
         *   - [^:\/\s]+: Matches any character except :, /, or whitespace.
         *
         * - port: Matches the port number if present.
         *   - (?::(?<port>\d+))?: Optionally matches a colon followed by one or more digits.
         *
         * - path: Matches the path component.
         *   - (?<path>\/[^?#\s]*)?: Optionally matches a / followed by any character except ?, #, or whitespace.
         *
         * - Anchors: ^ and $: Ensure the entire string is matched from start to end.
         */
        const urlMatcher = new RegExp(/^(?:(?<protocol>[a-zA-Z][a-zA-Z0-9+\-.]+):\/\/)?(?<host>[^:/\s]+)(?::(?<port>\d+))?(?<path>\/[^?#\s]*)?$/)
            .exec(url);

        return {
            protocol: (urlMatcher?.groups?.protocol && urlMatcher?.groups?.protocol !== '') ? urlMatcher?.groups?.protocol : null,
            host: (urlMatcher?.groups?.host && urlMatcher?.groups?.host !== '') ? urlMatcher?.groups?.host : null,
            path: (urlMatcher?.groups?.path && urlMatcher?.groups?.path !== '') ? urlMatcher?.groups?.path : '/',
            port: (urlMatcher?.groups?.port && urlMatcher?.groups?.port !== '') ? urlMatcher?.groups?.port : null,
        };
    }

    private async getServerInformationAndWorkingUrlForServer(serverUrl: string): Promise<AdvancedServerInformation> {
        const cleanUrl = this.cleanUrl(serverUrl, false);
        const validUrlObject = this.getUrlObjectByCleanUrl(cleanUrl);
        if (!validUrlObject) {
            throw (new ServerSelectionNoApiError());
        }

        const urlWithProtocol = validUrlObject.toString()
            .replace(/\/$/g, '');
        let serverInformation = await this.tryGetServerInformationByServerUrl(urlWithProtocol);
        if (serverInformation) {
            return {
                serverInformation,
                workingServerUrl: urlWithProtocol
            };
        }

        const urlWithoutProtocol = (validUrlObject.host + validUrlObject.pathname).replace(/\/$/g, '');
        serverInformation = await this.tryGetServerInformationByServerUrl(urlWithoutProtocol);
        if (serverInformation) {
            return {
                serverInformation,
                workingServerUrl: urlWithoutProtocol
            };
        }

        return await this.reachServerWithApiUrlParts(urlWithProtocol);
    }

    private async tryGetServerInformationByServerUrl(url: string): Promise<ServerInformation | undefined> {
        try {
            return await this.getServerInformation(url);
        } catch (error) {
            if (error instanceof SslCertificateError) {
                throw error;
            }
            return undefined;
        }
    }

    private async reachServerWithApiUrlParts(serverUrl: string): Promise<AdvancedServerInformation> {
        const urlMiddleParts = [...environment.apiUrlParts];

        while (urlMiddleParts.length > 0) {
            const urlParts = [
                serverUrl,
                ...urlMiddleParts
            ];
            const urlPath = secureProtocol + this.cleanUrl(urlParts.join('/'));

            try {
                const serverInformation = await this.getServerInformation(urlPath);
                return {
                    serverInformation,
                    workingServerUrl: urlPath
                };
            } catch (error) {
                if (error instanceof SslCertificateError) {
                    throw error;
                }
                urlMiddleParts.shift();
            }
        }

        throw (new ServerSelectionNoApiError());
    }

}
